// SPDX-FileCopyrightText: 2025 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useContext, useRef } from 'react';
import PreviewImage from './PreviewImage';
import { useTranslation } from 'react-i18next';
import { TagInput } from '../settings/subcomponents/TagInput';
import { useProjectTagManagement } from 'hooks/projectSettingsHooks/useProjectTagManagement';
import { postImageMeta } from '../label/metaDataApi';
import { useParams } from 'react-router';
import { ImagesContext } from '../images/ImagesContext';
import styled from 'styled-components';
import { LabelPackageInfoMessage } from '@tls/sw91-communication/types/com.api_db_access';
import { Dialog, FooterButton } from '@tls/treact-ui';

interface Props {
  labelPackage?: LabelPackageInfoMessage;
  isOpen: boolean;
  projectTags: string[];
  onClose: () => void;
}
export default function EditTagsDialog({ projectTags, labelPackage, isOpen, onClose }: Readonly<Props>) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { updateImageTags } = useContext(ImagesContext);

  const { id } = useParams();

  const { tags, selectedTag, allTagsAsObject, setSelectedTag, addSelectedTag, removeTag } = useProjectTagManagement(
    labelPackage?.tags ?? [],
    projectTags
  );

  const onSave = () => {
    if (!labelPackage?.imageMd5) return;
    postImageMeta(id, labelPackage.imageMd5, tags).then(() => {
      updateImageTags(labelPackage.imageMd5!, tags);
    });
    onClose();
  };
  if (!labelPackage) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} size='large' data-testid='data-edit-tags-dialog'>
      <Dialog.DialogHeader label={t('project.settings.tag.add')} />
      <Dialog.DialogContent ref={containerRef}>
        <div data-testid='data-edit-tags-dialog-image-name'>
          <b>{t('project.settings.tag.image')} </b>
          {labelPackage.imageName}
        </div>
        <ImageContainer data-testid={`data-image-${labelPackage.imageMd5 ?? 'unknown'}-edit-tags-preview`}>
          <PreviewImage previewData={labelPackage.previewData} />
        </ImageContainer>
        <TagInput
          tags={tags}
          selectedTag={selectedTag}
          setSelectedTag={setSelectedTag}
          addSelectedTag={addSelectedTag}
          removeTag={removeTag}
          allTagsAsObject={allTagsAsObject}
          containerRef={containerRef}
          t={t}
        />
      </Dialog.DialogContent>
      <Dialog.Footer
        right={[
          <FooterButton
            key='cancel'
            label={t('cancel')}
            onClick={onClose}
            variant='secondary'
            data-testid='data-edit-tags-dialog-cancel-button'
          />,
          <FooterButton
            key='save'
            label={t('save')}
            onClick={onSave}
            data-testid='data-edit-tags-dialog-save-button'
          />,
        ]}
      />
    </Dialog>
  );
}

const ImageContainer = styled.div`
  width: 40%;
  margin: 1rem auto;
`;
